import {
  MetalRef,
  TransactionRequestV4,
  TransactionStatusV4,
} from '@models/resources/transaction-request.model';

/**
 * V4-Demo-Daten für die Customer-Transaktions-Liste (Sprint 5c S5c-11).
 *
 * Wire-Shape matched `TransactionRequestResource` 1:1. Deckt alle 7 V4-
 * Status ab (Open, Consulted, Invoiced, Paid, Depot-Stored, Rejected,
 * Cancelled) damit die Status-Badges in der Liste End-to-End sichtbar
 * werden können. Snapshot-Felder werden statusabhängig befüllt:
 * - `open` / `consulted`: nur `reference_price`
 * - `invoiced`: + `invoice_total_eur` + `invoice_note`
 * - `paid`: + `purchase_date` + `purchase_fix_eur_per_gram`
 * - `depot_stored`: + `purchased_grams`
 * - `rejected` / `cancelled`: optionaler `note` mit Begründung
 *
 * Sprint 6 (S6-5): Admin-V4-Refactor nutzt dieselbe Liste plus ein
 * separates `TX_CUSTOMER_ID_BY_TX_ID`-Lookup, damit der Customer-Resource-
 * Type frei vom serverseitigen `customer_id`-Feld bleibt (Customer-
 * Endpoint scopt implizit per Sanctum-User).
 */

const METAL_GOLD: MetalRef = { id: 1, symbol: 'Au', name: 'Gold' };
const METAL_SILVER: MetalRef = { id: 2, symbol: 'Ag', name: 'Silber' };

interface FixtureOptions {
  status: TransactionStatusV4;
  invoice?: { total_eur: number; note: string | null };
  purchase?: { date: string; fix_eur_per_gram: number; grams: number };
}

function tx(
  id: number,
  metal: MetalRef,
  type: 'buy' | 'sell',
  amount_eur: number | null,
  requested_grams: number | null,
  reference_price: number,
  note: string | null,
  created_at: string,
  options: FixtureOptions,
): TransactionRequestV4 {
  return {
    id,
    metal,
    type,
    status: options.status,
    amount_eur,
    requested_grams,
    note,
    reference_price,
    invoice_total_eur: options.invoice?.total_eur ?? null,
    invoice_note: options.invoice?.note ?? null,
    purchase_date: options.purchase?.date ?? null,
    purchase_fix_eur_per_gram: options.purchase?.fix_eur_per_gram ?? null,
    purchased_grams: options.purchase?.grams ?? null,
    invoice: options.invoice
      ? {
          id,
          invoice_number: `RE-2026-${id.toString().padStart(6, '0')}`,
          amount_eur: options.invoice.total_eur,
          status: 'sent',
          type: 'transaction',
          generated_at: created_at,
          sent_at: created_at,
          paid_at: null,
          download_url: `/api/invoices/${id}/download`,
          recipient_email_override: null,
          recipient_name_override: null,
          recipient_address_override: null,
          recipient_pdf_version: 0,
          effective_recipient: {
            email: 'demo@goldapp.local',
            name: 'Demo Kunde',
            street: null,
            zip: null,
            city: null,
            country: null,
          },
        }
      : null,
    created_at,
    updated_at: created_at,
  };
}

export const DEMO_TRANSACTIONS_V4: TransactionRequestV4[] = [
  tx(101, METAL_GOLD, 'buy', 12_500, null, 82.08, 'Initiale Tranche', '2026-05-09T08:30:00+02:00', {
    status: 'open',
  }),
  tx(
    102,
    METAL_GOLD,
    'buy',
    5_000,
    null,
    82.08,
    'Nachkauf nach Marktbesprechung',
    '2026-05-06T10:15:00+02:00',
    {
      status: 'consulted',
    },
  ),
  tx(
    103,
    METAL_SILVER,
    'buy',
    1_500,
    null,
    1.185,
    'Silber als Diversifikation',
    '2026-05-02T14:42:00+02:00',
    {
      status: 'invoiced',
      invoice: { total_eur: 1_545, note: 'Rechnungsanschrift wie hinterlegt' },
    },
  ),
  tx(
    104,
    METAL_GOLD,
    'buy',
    8_000,
    null,
    81.5,
    null,
    '2026-04-28T11:05:00+02:00',
    {
      status: 'paid',
      invoice: { total_eur: 8_240, note: null },
      purchase: { date: '2026-04-30', fix_eur_per_gram: 81.94, grams: 0 },
    },
  ),
  tx(
    105,
    METAL_GOLD,
    'sell',
    null,
    150.0,
    81.94,
    'Teilverkauf für Renovierung',
    '2026-03-28T14:12:00+01:00',
    {
      status: 'depot_stored',
      invoice: { total_eur: 12_291, note: null },
      purchase: { date: '2026-04-01', fix_eur_per_gram: 81.94, grams: -150.0 },
    },
  ),
  tx(
    106,
    METAL_SILVER,
    'buy',
    500,
    null,
    1.21,
    'Testanfrage',
    '2026-03-20T09:00:00+01:00',
    {
      status: 'cancelled',
    },
  ),
  tx(
    107,
    METAL_GOLD,
    'sell',
    null,
    5000.0,
    82.08,
    'Versuch Großverkauf',
    '2026-02-14T16:20:00+01:00',
    {
      status: 'rejected',
    },
  ),
  // ---------------------------------------------------------------------
  // Sprint 8 Sell-Workflow-Demo-TX (4 Status: open, consulted, sold,
  // paid_out). Verteilt über die ersten 4 Customer-Stubs, damit der
  // Customer-Filter auch im Verkauf-Tab Daten zeigt.
  // ---------------------------------------------------------------------
  tx(
    201,
    METAL_GOLD,
    'sell',
    null,
    50.0,
    82.5,
    'Liquiditätsbedarf Q2',
    '2026-05-18T09:00:00+02:00',
    {
      status: 'open',
    },
  ),
  tx(
    202,
    METAL_SILVER,
    'sell',
    null,
    1200.0,
    1.21,
    'Diversifikation reduzieren',
    '2026-05-15T11:30:00+02:00',
    {
      status: 'consulted',
    },
  ),
  tx(
    203,
    METAL_GOLD,
    'sell',
    8_125.0,
    100.0,
    81.25,
    'Teilverkauf Goldbestand',
    '2026-05-10T14:00:00+02:00',
    {
      status: 'sold',
      purchase: { date: '2026-05-12', fix_eur_per_gram: 81.25, grams: -100.0 },
    },
  ),
  tx(
    204,
    METAL_GOLD,
    'sell',
    16_400.0,
    200.0,
    82.0,
    'Auszahlung abgeschlossen',
    '2026-04-22T10:00:00+02:00',
    {
      status: 'paid_out',
      purchase: { date: '2026-04-24', fix_eur_per_gram: 82.0, grams: -200.0 },
    },
  ),
];

/**
 * Admin-Side-Lookup: welche `transaction_request_id` gehört zu welchem
 * `customer_id`. Sprint 6 (S6-5) — die Admin-V4-Liste joint diese IDs
 * mit den `DEMO_ADMIN_CUSTOMERS`, damit das denormalisierte Wire-Shape
 * (`AdminTransactionListItemV4`) konsistent mit dem realen Backend
 * (`AdminTransactionRequestResource->whenLoaded('customer')`) bleibt.
 *
 * Aufteilung der 7 Demo-TX über die ersten 4 Customer-Stubs, damit Tests
 * mit Customer-Filter (`?customer=2`) auch Daten finden.
 */
export const TX_CUSTOMER_ID_BY_TX_ID: Readonly<Record<number, number>> = {
  101: 1,
  102: 1,
  103: 2,
  104: 2,
  105: 3,
  106: 3,
  107: 4,
  // Sprint 8 Sell-Workflow-Demo (jeweils einer pro Customer-Stub).
  201: 1,
  202: 2,
  203: 3,
  204: 4,
};
